"use client";
import React from "react";
// import { motion } from "motion/react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";

const transition = {
  type: "spring",
  mass: 0.5,
  damping: 11.5,
  stiffness: 100,
  restDelta: 0.001,
  restSpeed: 0.001,
};

/**
 * Modified MenuItem component.
 * - Opens dropdown on hover
 * - Keeps dropdown open if the mouse is over it; otherwise, it closes after 1 second.
 */
export const MenuItem = ({
  setActive,
  active,
  item,
  children,
}: {
  setActive: (item: string | null) => void;
  active: string | null;
  item: string;
  children?: React.ReactNode;
}) => {
  // Ref to store the timeout ID
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // When the mouse enters the menu item, cancel any pending timeout and open the dropdown.
  const handleItemMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActive(item);
  };

  // When the mouse leaves the menu item, start a 1-second timer to close the dropdown.
  const handleItemMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActive(null);
    }, 1000);
  };

  // When the mouse is over the children, cancel any pending timeout.
  const handleChildrenMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // When the mouse leaves the children, start a 1-second timer to close the dropdown.
  const handleChildrenMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActive(null);
    }, 1000);
  };

  return (
    <div
      onMouseEnter={handleItemMouseEnter}
      onMouseLeave={handleItemMouseLeave}
      className="relative cursor-pointer"
    >
      <motion.p
        transition={{ duration: 0.3 }}
        className="text-sm text-light-body-text hover:text-black"
      >
        {item}
      </motion.p>
      {active === item && (
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={transition}
        >
          <div
            onMouseEnter={handleChildrenMouseEnter}
            onMouseLeave={handleChildrenMouseLeave}
            className="absolute top-[calc(100%_+_1.2rem)] left-1/2 transform -translate-x-1/2 pt-4"
          >
            <motion.div
              transition={transition}
              layoutId="active" // layoutId ensures smooth animation
              className="bg-beige rounded-2xl overflow-hidden border border-white/20 shadow-xl"
            >
              <motion.div
                layout
                className="w-max h-full p-4"
                onClick={() => setActive(null)}
              >
                {children}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export const Menu = ({
  setActive,
  children,
}: {
  setActive: (item: string | null) => void;
  children: React.ReactNode;
}) => {
  return (
    <nav className="relative shadow-input flex justify-center items-center py-2">
      {children}
    </nav>
  );
};

export const ProductItem = ({
  title,
  description,
  href,
  src,
}: {
  title: string;
  description: string;
  href: string;
  src: string;
}) => {
  return (
    <Link href={href} className="flex bg-beige gap-4">
      <Image
        src={src}
        width={160}
        height={90}
        alt={title}
        className="shrink-0 rounded-md shadow-2xl"
      />
      <div className="max-w-[200px]">
        <h4 className="text-[16px] font-normal mb-2 text-light-main-heading">
          {title}
        </h4>
        <p className="text-light-body-text text-[14px] max-w-[20rem] font-normal">
          {description}
        </p>
      </div>
    </Link>
  );
};

export const HoveredLink = ({ children, ...rest }: any) => {
  return (
    <Link
      {...rest}
      className="text-neutral-700 dark:text-neutral-200 hover:text-black "
    >
      {children}
    </Link>
  );
};
